import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import useFetch from 'use-http';
import Personnel from 'src/models/personnel/Personnel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import User from 'src/models/User';
import DefaultPay from 'src/models/personnel/DefaultPay';
import DefaultBill from 'src/models/personnel/DefaultBill';
import moment from 'moment';

interface IAddPersonnelDialogProps {
    dialogName: string;
    personnelList: Personnel[];
    hideDialog: () => void;
}

const AddPersonnelDialog = (props: IAddPersonnelDialogProps) => {
    // const { register, handleSubmit, errors, setValue, reset } = useForm();
    const [employeeStatus, setEmployeeStatus] = useState<Boolean>(false);
    const { handleSubmit, register, errors, setValue, getValues } = useForm();
    const { get, post, response } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    const [users, setUsers] = useState<User[]>([]);

    // Initial render
    useEffect(() => {
        onCreate();
        
        return () => {
            onDestroy();
        };
    }, []);
    const onCreate = () => {
        getUsers();
    };

    const onDestroy = () => {
        console.log('AddPersonnelDialog onDestroy()');
    };

    const getUsers = async () => {
        const result = await get('v1.0/users/user-list');

        if (response.ok) {
            setUsers(result);
        }
    };

    const addPersonnel = async (data: Personnel) => {
        let values = getValues();

        const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i; // Regex pattern for email
        const ssnPattern = /^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/; // Regex pattern SSN
        let validated = true;
        let errorMessage = '';

        if (data.firstName.length === 0 || data.lastName.length === 0 || data.username.length === 0 || data.ssn.length === 0 || data.federalTaxId.length === 0) {
            validated = false;
            errorMessage += 'Required info missing\n';
        }

        if (props.personnelList.find(p => p.firstName === data.firstName && p.lastName === data.lastName)) {
            validated = false;
            errorMessage += `User (${data.firstName} ${data.lastName}) already exists\n`;
        } 

        if (users.find(u => u.username === data.username)) {
            validated = false;
            errorMessage += `A user with that email already exists\n`;
        } 

        if (props.personnelList.find(p => p.ssn === data.ssn)) {
            validated = false;
            errorMessage += `A user with that SSN already exists\n`;
        } 
        
        if (!emailPattern.test(data.username)) {
            validated = false;
            errorMessage += 'Invalid username / email (e.g. example@mail.com)\n';
        }
        
        if (!ssnPattern.test(data.ssn)) {
            validated = false;
            errorMessage += 'Invalid SSN (e.g. XXX-XX-XXXX)\n';
        }
        
        if (data.ssn !== values.verifySSN) {
            validated = false;
            errorMessage += 'SSNs do not match\n';
        }
        
        if (data.federalTaxId !== values.verifyTaxId) {
            validated = false;
            errorMessage += 'Tax Ids do not match';
        }

        if (validated) {
            let result = await post(`/v1.0/landmen/`, data);

            if (response.ok) {
                createDefaultPayUnit(finalizeDefaultPayUnit(result.id));
                createDefaultBillUnit(finalizeDefaultBillUnit(result.id));
                window.alert('Create Personnel successful');
            } else if (result?.messageCode === '15') {
                window.alert('Create Personnel failed');
            } else {
                window.alert('Create Personnel failed');
            }
        } else {
            window.alert(errorMessage);
        }
    };

    const finalizeDefaultPayUnit = (id: number) => {
        let values = getValues();
        console.log(values);

        let finalData: DefaultPay = {
            id: 0,
            userId: id,
            defaultPayRate: 0,
            defaultPayUnit: values?.defaultPayUnit,
            isActive: true,
            createdDate: moment(new Date).format('YYYY-MM-DD') as unknown as Date,
            createdBy: appContext.currentUser.username
        };

        return finalData;
    };

    const createDefaultPayUnit = async (data: DefaultPay) => {
        console.log(data);
        if (data?.defaultPayUnit) {
            let result = await post(`/v1.0/landmen/${data?.userId}/create-personnel-defaultPay`, data);

            if (response.ok) {
                // getDefaultPayHistoryByUserId();
                window.alert('Update successful');
            } else if (result?.messageCode === '15') {
                window.alert('Update failed');
            } else {
                window.alert('Update failed');
            }
        }
    };

    const finalizeDefaultBillUnit = (id: number) => {
        let values = getValues();
        console.log(values);

        let finalData: DefaultBill = {
            id: 0,
            userId: id,
            defaultBillRate: 0,
            defaultBillUnit: values?.defaultBillUnit,
            isActive: null,
            createdDate: moment(new Date).format('YYYY-MM-DD') as unknown as Date,
            createdBy: appContext.currentUser.username
        };

        return finalData;
    };

    const createDefaultBillUnit = async (data: DefaultBill) => {
        console.log(data);
        if (data?.defaultBillUnit) {
            let result = await post(`/v1.0/landmen/${data?.userId}/create-personnel-defaultBill`, data);

            if (response.ok) {
                // getDefaultPayHistoryByUserId();
                window.alert('Update successful');
            } else if (result?.messageCode === '15') {
                window.alert('Update failed');
            } else {
                window.alert('Update failed');
            }
        }
    };

    const checkStatus = (e: React.FormEvent<HTMLSelectElement>) => {
        if (e.currentTarget.value === 'Employee') { 
            setEmployeeStatus(true);
        } else { 
            setValue('title', ''); 
            setEmployeeStatus(false);
        }
    };

    const onSubmit = async (data: Personnel) => {
        console.log('Add Personnel Dialog OnSubmit :D');
        addPersonnel(data);
    };

    return (
        <Dialog title="Add Personnel" dialogName={props.dialogName} bodyStyle={{ width: '50%' }}>
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="form-row">
                        <RHFSelect
                            bodyStyle={{ marginBottom: '1rem' }}
                            name="status"
                            label="Status"
                            onChange={(e) => checkStatus(e)}
                            options={[
                                { value: 'Bench', label: 'Bench' },
                                { value: 'Contractor', label: 'Contractor' },
                                { value: 'Employee', label: 'Employee' },
                                { value: 'Landman Apprentice', label: 'Landman Apprentice' },
                                { value: 'Archive', label: 'Archive' },
                                { value: 'Cannot Locate', label: 'Cannot Locate' },
                                { value: 'Subcontractor', label: 'Subcontractor' },
                            ]}
                            register={register}
                            errors={errors}
                        />
                        {employeeStatus === true && <RHFInputBox
                            style={{ width: '100%' }}
                            placeholder="Employee Title"
                            name="title"
                            label=" "
                            register={register}
                            errors={errors}
                        />}
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="firstName"
                            label="First Name"
                            isRequired={true}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="lastName"
                            label="Last Name"
                            isRequired={true}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="middleName"
                            label="Middle Name"
                            register={register}
                            errors={errors}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="corporationName"
                            label="Corporation"
                            className="lh-half-width"
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="username"
                            label="Username / Email"
                            className="lh-half-width"
                            register={register}
                            isRequired={true}
                            errors={errors}
                        />
                        <RHFSelect
                            style={{ width: '100%' }}
                            name="billingOffice"
                            label="Office"
                            options={[
                                { value: 'Denver', label: 'Denver' },
                                { value: 'Katy', label: 'Katy' },
                                { value: 'McMurray', label: 'McMurray' },
                                { value: 'OKC', label: 'Oklahoma City' },
                                { value: 'Renewables', label: 'Renewables' },
                            ]}
                            register={register}
                            errors={errors}
                        />
                        <RHFSelect
                            style={{ width: '100%' }}
                            name="irsCategoryType"
                            label="IRS Category Type"
                            options={[
                                { value: '1', label: 'IRS Category Type Option' },

                            ]}
                            register={register}
                            errors={errors}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="ssn"
                            label="Social Security Number"
                            register={register}
                            errors={errors}
                            onPaste={(e) => { 
                                e.preventDefault();
                            }}
                        />
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="verifySSN"
                            label="Verify"
                            register={register}
                            errors={errors}
                            onPaste={(e) => { 
                                e.preventDefault();
                            }}
                        />
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="federalTaxId"
                            label="Federal Tax Id"
                            register={register}
                            errors={errors}
                            onPaste={(e) => { 
                                e.preventDefault();
                            }}
                        />
                        <RHFInputBox
                            style={{ width: '100%' }}
                            name="verifyTaxId"
                            label="Verify"
                            register={register}
                            errors={errors}
                            onPaste={(e) => { 
                                e.preventDefault();
                            }}
                        />
                    </div>
                    <div className="form-row" style={{marginLeft: '30.65%'}}>
                        <RHFSelect 
                            name={`defaultPayUnit`}
                            placeholder="Default Pay Unit"
                            options={[
                                { value: 'Day', label: 'Day' },
                                { value: 'Hour', label: 'Hour' },
                                { value: 'Month', label: 'Month' },
                            ]}
                            style={{width: '100%'}}
                            bodyStyle={{width: '27%'}}
                            register={register} 
                            errors={errors}
                            title="Default Pay Unit"
                        />
                        <RHFSelect 
                            name={`defaultBillUnit`}
                            placeholder="Default Bill Unit"
                            options={[
                                { value: 'Day', label: 'Day' },
                                { value: 'Hour', label: 'Hour' },
                                { value: 'Month', label: 'Month' },
                            ]}
                            style={{width: '100%'}}
                            bodyStyle={{width: '27%'}}
                            register={register} 
                            errors={errors}
                            title="Default Bill Unit"
                        />
                    </div>
                    <div className="form-row" style={{marginTop: '2rem', marginLeft: '33.5%', textAlign: 'center'}}>
                        <Button
                            className="blue-hover-button"
                            style={{ width: '24%', marginRight: '1rem' }}
                            text={'OK'}
                            type="submit"
                        />
                        <Button
                            className="blue-hover-button"
                            style={{ width: '24%', marginRight: '1rem' }}
                            text={'Cancel'}
                            onClick={() => props.hideDialog()}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

export default AddPersonnelDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/AddPersonnelDialog.tsx